/**
 * Merchant FAQ — Cyannect (Loop P4-FAQ)
 *
 * Route: /merchant/faq
 * Renders inside the merchant PortalShell (burgundy header, sidebar) so
 * it inherits Cyannect's design system with zero new dependencies.
 *
 * Design goals:
 *   - Fully client-side accordion using native <details>/<summary> (no
 *     new UI dependency, keyboard accessible, works on mobile).
 *   - Search box that filters questions in-place across all sections.
 *   - Sticky section nav for quick jumping on desktop.
 *   - Content mirrors the actual product configuration in
 *     `services/billing/catalog.py` — verified via the consistency table
 *     in the merchant PRD.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { usePricing } from "@/lib/pricing";
import { finalFaqSections } from "@/pages/merchant/faqPricing";


function AskCyannectCard() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {matched, answer, question_id, canonical_intent, source, escalate}
  const [voted, setVoted] = useState(false);

  const ask = async () => {
    const trimmed = q.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setResult(null);
    setVoted(false);
    try {
      const r = await api.post("/merchant/support/ask", { question: trimmed });
      setResult(r.data);
    } catch (e) {
      setResult({ matched: false, escalate: true, answer: null,
                  error: e?.response?.data?.detail || "Something went wrong. Try again." });
    } finally {
      setLoading(false);
    }
  };

  const vote = async (kind) => {
    if (!result?.question_id || voted) return;
    setVoted(true);
    try {
      await api.post(`/merchant/support/feedback/${result.question_id}`, { vote: kind });
    } catch { /* silent */ }
  };

  return (
    <div className="pf-card mb-6" data-testid="ask-cyannect-card">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-widest text-burgundy-600 font-semibold">Ask Cyannect</span>
        <span className="text-[10px] uppercase tracking-widest text-slate-400">Beta · deterministic answers only</span>
      </div>
      <p className="text-sm text-slate-500 mb-3">
        Type a support question. We match your question against approved answers only — no AI is called in this loop.
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="e.g. How do I add Alberta?"
          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
          data-testid="ask-cyannect-input"
        />
        <button
          onClick={ask}
          disabled={loading || !q.trim()}
          data-testid="ask-cyannect-submit"
          className="px-4 py-2 rounded-lg bg-burgundy-600 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {loading ? "Asking…" : "Ask"}
        </button>
      </div>

      {result && (
        <div className="mt-4">
          {result.matched && result.answer ? (
            <div data-testid="ask-cyannect-result-matched" className="p-3 rounded-xl bg-white/70 border border-slate-100">
              <div className="text-[10px] uppercase tracking-widest font-semibold mb-1"
                   style={{ color: result.source === "LLM_GENERATED" ? "#a16207" : "#059669" }}>
                {result.source === "LLM_GENERATED"
                  ? "AI answer · not yet approved"
                  : `Approved answer · ${result.canonical_intent}`}
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{result.answer}</p>
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={() => vote("helpful")}
                  disabled={voted}
                  data-testid="ask-cyannect-helpful"
                  className="text-xs px-2 py-1 rounded-md border border-slate-200 hover:bg-emerald-50 disabled:opacity-40"
                >
                  👍 Helpful
                </button>
                <button
                  onClick={() => vote("not_helpful")}
                  disabled={voted}
                  data-testid="ask-cyannect-unhelpful"
                  className="text-xs px-2 py-1 rounded-md border border-slate-200 hover:bg-red-50 disabled:opacity-40"
                >
                  👎 Not helpful
                </button>
                {voted && <span className="text-xs text-slate-400">Thanks — logged.</span>}
              </div>
            </div>
          ) : (
            <div data-testid="ask-cyannect-result-escalate" className="p-3 rounded-xl bg-white/70 border border-slate-100">
              <p className="text-sm text-slate-700">
                I don't have a confirmed answer for that yet. You can{" "}
                <a href="mailto:support@cyannect.com" className="text-burgundy-700 font-semibold hover:underline">
                  contact Cyannect Support
                </a>.
              </p>
              {result.error && <p className="mt-1 text-xs text-red-600">{result.error}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
import {
  Search, Store, MapPin, DollarSign, Tag, ShieldAlert, Info,
  Percent, Calendar, Repeat, Layers, Wrench, BarChart3, HelpCircle, Rocket,
} from "lucide-react";

// ─── Section + Q&A data ─────────────────────────────────────────────
const SECTIONS = [
  {
    id: "storefronts",
    title: "Storefronts",
    icon: Store,
    intro:
      "A Storefront is your shopper-facing branded presence inside Cyannect.",
    items: [
      {
        q: "What is a Cyannect Storefront?",
        a: [
          "A Storefront is a shopper-facing branded business presence inside Cyannect. A Storefront may represent a distinct:",
          "• brand",
          "• division",
          "• service line",
          "• business unit",
          "• banner",
          "• independently managed catalogue",
          "",
          "A Storefront is NOT simply a product category.",
        ],
      },
      {
        q: "Is a Storefront the same as a product category?",
        a: [
          "No. Categories belong INSIDE a Storefront.",
          "",
          "Example:",
          "Merchant Account: Staples Canada",
          "Storefront: Staples",
          "Categories may include:",
          "• Computers",
          "• Office Supplies",
          "• Furniture",
          "• Ink & Toner",
          "",
          "A merchant should not normally create separate Storefronts just for each product category.",
        ],
      },
      {
        q: "When should I create another Storefront?",
        a: [
          "A new Storefront makes sense when your business operates a distinct:",
          "• brand",
          "• service line",
          "• division",
          "• independently managed catalogue",
          "• customer experience",
          "",
          "Examples:",
          "Walmart Canada",
          "  → Walmart",
          "  → Vision Centre",
          "  → Pharmacy",
          "",
          "Staples Canada",
          "  → Staples",
          "  → Print / Solutionshop",
          "  → Staples Business",
          "",
          "Do not use Storefronts merely to split normal product categories.",
        ],
      },
    ],
  },
  {
    id: "starter",
    title: "Starter plan",
    icon: Tag,
    intro:
      "Starter includes 1 Storefront and 1 Province on that Storefront.",
    items: [
      {
        q: "How many Storefronts are included with Starter?",
        a: [
          "Starter includes:",
          "• 1 Storefront",
          "• 1 Province included on that Storefront",
          "",
          "Example:",
          "ABC Fashion → Ontario is included.",
        ],
      },
      {
        q: "Can my Starter Storefront operate in another province?",
        a: [
          "Yes.",
          "Additional province: CA$11.99/month per additional province.",
          "",
          "Example:",
          "ABC Fashion",
          "• Ontario — Included",
          "• Alberta — + CA$11.99/month",
          "• British Columbia — + CA$11.99/month",
          "",
          "Province add-ons apply to the specific Storefront.",
        ],
      },
      {
        q: "Can Starter merchants create another Storefront?",
        a: [
          "Yes.",
          "Additional Storefront: CA$19.99/month.",
          "",
          "Each additional Starter Storefront bundles up to 3 provinces — same allowance as Growth. Extra provinces beyond those 3 are CA$11.99/month each per Storefront.",
        ],
      },
    ],
  },
  {
    id: "growth",
    title: "Growth plan",
    icon: Layers,
    intro:
      "Growth includes up to 3 Storefronts and up to 3 Provinces on each Storefront.",
    items: [
      {
        q: "How many Storefronts are included with Growth?",
        a: [
          "Growth includes:",
          "• Up to 3 Storefronts",
          "• Each included Storefront may operate in up to 3 Provinces without additional province charges.",
        ],
      },
      {
        q: "What happens if a Growth Storefront needs more than 3 Provinces?",
        a: [
          "Additional Province: CA$11.99/month per additional province per Storefront.",
          "",
          "Example:",
          "Storefront A already uses:",
          "• Ontario",
          "• Alberta",
          "• British Columbia",
          "Merchant adds: Quebec",
          "Additional charge: CA$11.99/month.",
        ],
      },
      {
        q: "Can Growth merchants create more than 3 Storefronts?",
        a: [
          "Yes.",
          "Additional Storefront: CA$19.99/month.",
          "",
          "Each additional Growth Storefront includes up to 3 Provinces. Additional provinces beyond those 3 are CA$11.99/month per province.",
        ],
      },
    ],
  },
  {
    id: "anti-abuse",
    title: "Store type & anti-abuse",
    icon: ShieldAlert,
    intro:
      "Additional Storefronts must represent legitimate distinct brands or business experiences.",
    items: [
      {
        q: "Can I create duplicate Storefronts to get more provinces?",
        a: [
          "No.",
          "",
          "Additional Storefronts should represent legitimate distinct brands, divisions, service lines, or independently managed shopper experiences.",
          "",
          "Merchants should not create duplicate or substantially identical Storefronts mainly to:",
          "• avoid province add-on charges",
          "• bypass plan allowances",
          "• duplicate marketplace visibility",
          "• gain unfair search exposure",
          "",
          "Cyannect may review duplicate Storefronts and require appropriate correction.",
        ],
      },
    ],
  },
  {
    id: "provinces",
    title: "Provinces",
    icon: MapPin,
    intro:
      "Province allowances apply per Storefront — not account-wide.",
    items: [
      {
        q: "Do I need a separate Storefront for every province?",
        a: [
          "No. A single Storefront can operate in multiple provinces.",
          "",
          "Example:",
          "ABC Fashion",
          "  → Ontario",
          "  → Alberta",
          "  → British Columbia",
          "",
          "This remains one Storefront.",
        ],
      },
      {
        q: "Are province allowances account-wide?",
        a: [
          "No. Province allowances apply PER STOREFRONT.",
          "",
          "Example (Growth merchant):",
          "Storefront A: Ontario + Alberta + BC",
          "Storefront B: Ontario + Alberta + BC",
          "",
          "Each Storefront is independently using its 3 included provinces.",
        ],
      },
      {
        q: "Can an offer target only some provinces?",
        a: [
          "Yes. An offer may target one or more provinces enabled for its parent Storefront.",
          "",
          "Example — Storefront enabled in Ontario + Alberta + BC:",
          "• Offer A: Ontario only",
          "• Offer B: Ontario + Alberta",
          "• Offer C: BC only",
          "",
          "An offer cannot target a province that is not enabled for the Storefront.",
        ],
      },
    ],
  },
  {
    id: "taxes",
    title: "Taxes",
    icon: Percent,
    intro:
      "Cyannect controls Storefront geographic availability. Your checkout system handles applicable taxes.",
    items: [
      {
        q: "Does Cyannect calculate sales tax for my customers?",
        a: [
          "No.",
          "",
          "Cyannect Storefront province settings control geographic availability WITHIN Cyannect. The merchant's own checkout/payment system remains responsible for applicable taxes such as:",
          "• GST",
          "• HST",
          "• PST",
          "• QST",
          "• and any other tax obligations applicable to the merchant's sale.",
        ],
      },
    ],
  },
  {
    id: "monthly-addons",
    title: "Monthly add-ons",
    icon: Calendar,
    intro: "Monthly base plan → monthly add-ons.",
    items: [
      {
        q: "How are add-ons billed if my main plan is monthly?",
        a: [
          "If your Starter or Growth plan is monthly:",
          "• Additional Province: CA$11.99/month",
          "• Additional Storefront: CA$19.99/month",
          "plus applicable taxes.",
        ],
      },
    ],
  },
  {
    id: "annual-addons",
    title: "Annual add-ons",
    icon: Repeat,
    intro:
      "Annual base plan → annual-only add-ons with a flat 15% discount.",
    items: [
      {
        q: "How are add-ons billed if my main plan is annual?",
        a: [
          "If your base plan is annual:",
          "• Storefront and Province add-ons must also be purchased annually.",
          "• Monthly add-ons are not available with an annual base subscription.",
          "• Annual add-ons receive a 15% discount on the standard 12-month add-on value.",
        ],
      },
      {
        q: "What is the annual price of an additional province?",
        a: [
          "Monthly reference price: CA$11.99",
          "12-month value: CA$143.88",
          "After 15% annual discount: CA$122.30/year",
          "before applicable taxes.",
        ],
      },
      {
        q: "What is the annual price of an additional Storefront?",
        a: [
          "Monthly reference price: CA$19.99",
          "12-month value: CA$239.88",
          "After 15% annual discount: CA$203.90/year",
          "before applicable taxes.",
        ],
      },
      {
        q: "Can I buy monthly add-ons if my plan is annual?",
        a: [
          "No.",
          "Annual base-plan merchants must purchase Storefront and Province add-ons annually.",
        ],
      },
      {
        q: "Do annual add-on discounts stack with other discounts?",
        a: [
          "Not automatically.",
          "The standard annual add-on discount is 15%.",
          "Any other promotion must be expressly offered by Cyannect.",
        ],
      },
    ],
  },
  {
    id: "upgrades",
    title: "Upgrades & downgrades",
    icon: BarChart3,
    intro:
      "Cyannect never silently deletes your Storefronts, offers, or historical analytics.",
    items: [
      {
        q: "What happens if I upgrade from Starter to Growth?",
        a: [
          "Growth's included Storefront and province allowances apply after the upgrade becomes effective. Where an existing paid add-on becomes included in Growth, the billing system applies the currently implemented upgrade treatment.",
        ],
      },
      {
        q: "What happens if I downgrade from Growth to Starter?",
        a: [
          "Cyannect will not automatically delete Storefronts, offers, or historical analytics.",
          "",
          "If your usage exceeds Starter allowances, you may need to:",
          "• disable excess Storefronts",
          "• reduce province coverage",
          "• or purchase the required add-ons",
          "",
          "before the affected Storefronts/offers can remain active.",
        ],
      },
    ],
  },
  {
    id: "redemption",
    title: "Automatic Discount vs Coupon Code",
    icon: Tag,
    intro:
      "Cyannect supports two merchant-selectable redemption methods.",
    items: [
      {
        q: "What redemption methods does Cyannect support?",
        a: [
          "Automatic Discount",
          "The shopper does NOT manually enter a coupon code. The shopper clicks the offer in Cyannect, the merchant integration identifies the eligible offer, and the merchant's checkout automatically applies the approved discount to eligible products. This provides the smoothest shopper experience but requires a one-time merchant checkout integration.",
          "",
          "Coupon Code",
          "Cyannect assigns or generates a coupon code. The shopper copies/enters the code at the merchant's checkout. The merchant validates the code using the Cyannect integration and applies the eligible discount. This is generally simpler for merchants to implement.",
        ],
      },
      {
        q: "Which redemption method should I choose?",
        a: [
          "Choose Automatic Discount if you want the smoothest customer experience and can complete the Cyannect checkout integration.",
          "",
          "Choose Coupon Code if you prefer a simpler integration where customers manually enter the Cyannect coupon.",
          "",
          "Both are supported Cyannect options.",
        ],
      },
    ],
  },
  {
    id: "integration",
    title: "Integration",
    icon: Wrench,
    intro:
      "Integrate Cyannect once — reuse across every Storefront and offer. A merchant mobile app is optional.",
    items: [
      {
        q: "Do I need a mobile app to join Cyannect?",
        a: [
          "No.",
          "A merchant mobile app is optional.",
          "If customers can purchase products or services through your website, Cyannect can send shoppers directly to your website. With the appropriate Cyannect integration, referrals and completed purchases can be tracked without requiring a merchant mobile app.",
        ],
      },
      {
        q: "What if I only have a website?",
        a: [
          "That is fully supported.",
          "Your customers can:",
          "• discover your offer through Cyannect",
          "• click through to your website",
          "• complete checkout on your website",
          "• pay using your existing payment system",
          "Cyannect tracks the referral and verified conversion through the applicable integration.",
        ],
      },
      {
        q: "What if I have both a website and an app?",
        a: [
          "You may configure both. Where supported, Cyannect can use app / deep-link destinations while keeping your website as the fallback or alternate destination. Set your preference under Storefront → Where customers pay.",
        ],
      },
      {
        q: "Can Automatic Discount work on my website?",
        a: [
          "Yes.",
          "Automatic Discount can operate through a supported website checkout integration. A merchant mobile app is not required.",
        ],
      },
      {
        q: "Can Coupon Code work on my website?",
        a: [
          "Yes.",
          "Customers may enter the Cyannect code during your website checkout. A merchant mobile app is not required.",
        ],
      },
      {
        q: "Do I need to integrate Cyannect separately for every offer?",
        a: [
          "No. The intended model is:",
          "• Integrate Cyannect once",
          "• Create multiple Storefronts where eligible",
          "• Create multiple offers",
          "• Select Automatic Discount or Coupon Code",
          "• Use the same underlying Cyannect integration.",
          "",
          "A merchant should not normally require new developer work for every offer.",
        ],
      },
      {
        q: "Does Cyannect process shopper payments?",
        a: [
          "No.",
          "The shopper completes payment on the merchant's own website/app using the merchant's existing payment provider. Cyannect tracks eligible referrals/conversions through approved merchant integrations.",
        ],
      },
      {
        q: "Who applies the actual discount?",
        a: [
          "The merchant's checkout system applies the final discount. Cyannect provides the validated offer/discount eligibility information.",
        ],
      },
    ],
  },
  {
    id: "tracking",
    title: "Conversion tracking",
    icon: BarChart3,
    intro:
      "Cyannect measures verified conversions reported by your integration.",
    items: [
      {
        q: "How does Cyannect know when a purchase is completed?",
        a: [
          "The merchant's approved integration reports the confirmed conversion to Cyannect after successful payment.",
          "",
          "Cyannect uses this information for:",
          "• conversion analytics",
          "• attributed revenue",
          "• applicable performance fees",
          "• shopper cashback where applicable",
          "",
          "A click or checkout alone is not treated as a completed sale.",
        ],
      },
    ],
  },
  {
    id: "analytics",
    title: "Analytics",
    icon: BarChart3,
    intro:
      "Depending on the integration and offer type, Cyannect can surface a range of merchant metrics.",
    items: [
      {
        q: "What can merchants track?",
        a: [
          "Depending on the integration and offer type, Cyannect may provide metrics such as:",
          "• Offer views",
          "• Clicks",
          "• Merchant visits",
          "• Checkout activity",
          "• Payment attempts",
          "• Verified conversions",
          "• Attributed revenue",
          "• Refunds / voids",
          "• Conversion performance",
        ],
      },
    ],
  },
  {
    id: "examples",
    title: "Storefront examples",
    icon: Info,
    intro:
      "How large merchants map their business into Cyannect Storefronts.",
    items: [
      {
        q: "Example: large merchant with multiple business lines",
        a: [
          "Merchant Account: Staples Canada",
          "",
          "Possible Storefronts:",
          "",
          "1) Staples",
          "   Categories: Computers, Office Supplies, Furniture, Ink & Toner",
          "",
          "2) Staples Print / Solutionshop",
          "   Categories: Business Cards, Posters, Printing, Marketing Services",
          "",
          "3) Staples Business",
          "   Categories: Commercial, Education, Enterprise",
          "",
          "This demonstrates why Storefronts represent distinct business experiences rather than individual product categories.",
        ],
      },
    ],
  },
  {
    id: "checkout-verification",
    title: "Coupon checkout verification",
    icon: ShieldAlert,
    intro:
      "Coupon Code offers require a manual review of your checkout so shoppers can actually use the codes.",
    items: [
      {
        q: "Why does Cyannect verify my checkout for Coupon Code offers?",
        a: [
          "If you use Coupon Code redemption, shoppers need a visible field where they can enter or paste the code during checkout. Cyannect verifies the checkout experience to help ensure shoppers can actually use the offers merchants publish.",
          "",
          "Automatic Discount offers are unaffected — they don't display a code to the shopper, so no coupon-entry field is required.",
        ],
      },
      {
        q: "What if my checkout does not have a coupon field?",
        a: [
          "You can use Automatic Discount where supported, or update your checkout to provide a promo/coupon-code entry field and submit it for verification.",
          "",
          "You'll find the Checkout Verification screen under \"Checkout verification\" in the merchant portal sidebar.",
        ],
      },
      {
        q: "What if my promo-code field appears only after adding an item?",
        a: [
          "Submit your cart or checkout URL and provide instructions explaining how to reach the coupon field. Cyannect can review the flow manually.",
          "",
          "Example instructions:",
          "\"Add any item to the cart, open the cart page, and click 'Have a promo code?' — the field appears below the order summary.\"",
        ],
      },
      {
        q: "What are the checkout verification statuses?",
        a: [
          "• Verification required — you haven't submitted your checkout yet. New Coupon Code offers are blocked until you do.",
          "• Under review — Cyannect has your details and is confirming the coupon-entry field.",
          "• Checkout verified — Coupon Code offers are unlocked for this storefront.",
          "• Coupon Code not supported — Cyannect couldn't find a coupon-entry field. Switch to Automatic Discount, or update your checkout and resubmit.",
          "• Legacy campaign — the offer was already live before verification launched. It keeps running, but any new Coupon Code offer still requires a fresh verification.",
        ],
      },
      {
        q: "Does verification affect existing live campaigns?",
        a: [
          "No. Live Coupon Code campaigns that pre-date the verification requirement are grandfathered and keep running. If you materially change the checkout destination or redemption method, you'll be asked to re-verify before publishing again.",
        ],
      },
    ],
  },
  {
    id: "onboarding",
    title: "Getting started with your first Storefront",
    icon: Rocket,
    intro:
      "Set up your business identity, shopper destination, coverage and readiness before creating your first Offer.",
    items: [
      {
        q: "How do I create my first Storefront?",
        a: [
          "Follow the guided Storefront Setup Guide:",
          "• Add your business identity — Storefront name, logo, description, primary category.",
          "• Configure your shopper destination — your website (a functioning website checkout is enough — you do not need your own mobile app).",
          "• Select Province Coverage — start with the regions that matter most to you today.",
          "• Add categories to organize your products or services inside the Storefront.",
          "• Review your integration readiness — referral tracking, conversion tracking, checkout verification for Coupon Code offers.",
          "• Review your Storefront summary and, when it's ready, create your first Offer.",
          "",
          "You can access the guide from the dashboard onboarding card, or from Storefronts → Storefront Setup Guide.",
        ],
      },
      {
        q: "Should I create another Storefront for every category?",
        a: [
          "No. A Storefront represents a brand, division, service line, or independently managed catalogue. Categories organize products inside a Storefront.",
          "",
          "Retail example — Storefront: ABC Fashion → Categories: Men, Women, Shoes.",
          "SaaS example — Storefront: Example AI → Categories: Subscriptions, Credit Packs, Add-ons.",
          "",
          "Separate Storefronts are appropriate for genuinely distinct business lines (for example \"ABC Fashion\" and \"ABC Print\") — not for individual product categories.",
        ],
      },
      {
        q: "Do I need my own mobile app to accept Cyannect Offers?",
        a: [
          "No. A functioning website checkout is sufficient. If you also have a Merchant mobile app, you can configure external destinations for it, but this is optional and never required.",
        ],
      },
      {
        q: "What happens after my Storefront is ready?",
        a: [
          "Once your Storefront is Ready, click Create First Offer to enter the Manual Campaign creator. You'll build a Draft, review the details on the Review page, confirm and Publish — the Offer goes live immediately for eligible shoppers.",
        ],
      },
    ],
  },
];


// ─── FAQ page ───────────────────────────────────────────────────────
export default function MerchantFAQ() {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const q = query.trim().toLowerCase();
  const pricing = usePricing();
  const sections = useMemo(() => (pricing?.final_active ? finalFaqSections(SECTIONS, pricing) : SECTIONS), [pricing]);

  const filteredSections = useMemo(() => {
    if (!q) return sections.map((s) => ({ ...s, hits: s.items }));
    return sections
      .map((s) => ({
        ...s,
        hits: s.items.filter((it) =>
          it.q.toLowerCase().includes(q) ||
          (Array.isArray(it.a) ? it.a.join(" ") : it.a).toLowerCase().includes(q)
        ),
      }))
      .filter((s) => s.hits.length > 0 || s.title.toLowerCase().includes(q));
  }, [q, sections]);

  const totalHits = filteredSections.reduce((n, s) => n + (s.hits?.length || 0), 0);

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="merchant-faq-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <HelpCircle className="w-8 h-8 text-burgundy-600" />
          <h1 className="font-display text-3xl md:text-4xl text-slate-900" data-testid="merchant-faq-title">
            Merchant FAQ
          </h1>
        </div>
        <p className="text-slate-600 mt-2 max-w-3xl">
          Everything you need to know about Cyannect Storefronts, provinces,
          plans, add-ons, and how offers work. If you need something we
          haven't covered here,{" "}
          <a href="mailto:hello@cyannect.com" className="text-burgundy-700 font-semibold hover:underline">
            hello@cyannect.com
          </a>.
        </p>
      </div>

      {/* MERCHANT-SUPPORT-KNOWLEDGE-1 Phase A · Ask Cyannect */}
      <AskCyannectCard />

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the FAQ (e.g. province, coupon, annual, storefront)…"
          data-testid="merchant-faq-search"
          className="w-full rounded-full border border-slate-300 bg-white pl-10 pr-4 py-3 text-sm focus:ring-2 focus:ring-burgundy-500 focus:border-burgundy-500"
        />
      </div>

      {/* Layout */}
      <div className="grid md:grid-cols-[220px_1fr] gap-8">
        {/* Section nav (desktop only) */}
        <nav className="hidden md:block sticky top-6 self-start" data-testid="merchant-faq-nav">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
            Sections
          </div>
          <ul className="space-y-1 text-sm">
            {filteredSections.map((s) => {
              const Icon = s.icon || DollarSign;
              return (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="flex items-center gap-2 py-1 px-2 rounded hover:bg-slate-100 text-slate-700"
                    data-testid={`merchant-faq-nav-${s.id}`}
                  >
                    <Icon className="w-3.5 h-3.5 text-burgundy-600" />
                    {s.title}
                  </a>
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-slate-400 mt-4 leading-snug">
            Prices in CAD, before applicable taxes. Cyannect maintains this
            FAQ. For quotes and account-level questions, contact{" "}
            <a href="mailto:hello@cyannect.com" className="underline">hello@cyannect.com</a>.
          </p>
        </nav>

        {/* Content */}
        <div className="space-y-8">
          {q && (
            <div
              className="text-sm text-slate-500"
              data-testid="merchant-faq-search-result-count"
            >
              {totalHits === 0
                ? `No matches for "${query}".`
                : `${totalHits} match${totalHits === 1 ? "" : "es"} for "${query}".`}
            </div>
          )}
          {filteredSections.map((s) => {
            const Icon = s.icon || DollarSign;
            return (
              <section
                key={s.id}
                id={s.id}
                data-testid={`merchant-faq-section-${s.id}`}
                className="scroll-mt-24"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-5 h-5 text-burgundy-600" />
                  <h2 className="font-display text-2xl text-slate-900">
                    {s.title}
                  </h2>
                </div>
                {s.intro && (
                  <p className="text-sm text-slate-600 mb-3">{s.intro}</p>
                )}
                <div className="space-y-3">
                  {(s.hits || s.items).map((it, i) => {
                    const key = `${s.id}-${i}`;
                    const isOpen = openId === key;
                    return (
                      <details
                        key={key}
                        open={isOpen || !!q}
                        onToggle={(e) => {
                          if (e.currentTarget.open) setOpenId(key);
                          else if (openId === key) setOpenId(null);
                        }}
                        data-testid={`merchant-faq-item-${key}`}
                        className="group rounded-xl bg-white border border-slate-200 p-4 open:shadow-sm open:border-burgundy-200"
                      >
                        <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                          <span className="font-medium text-slate-900">{it.q}</span>
                          <span className="text-burgundy-700 text-xl transition-transform group-open:rotate-45 shrink-0 leading-none">
                            +
                          </span>
                        </summary>
                        <div className="mt-3 text-slate-700 text-sm leading-relaxed space-y-1">
                          {(Array.isArray(it.a) ? it.a : [it.a]).map((line, li) =>
                            line === "" ? (
                              <div key={li} className="h-2" aria-hidden />
                            ) : (
                              <div key={li}>{line}</div>
                            )
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </section>
            );
          })}

          <div className="pf-card p-5 bg-cream-50 border border-cream-200" data-testid="merchant-faq-still-stuck">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-burgundy-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-900">Still stuck?</div>
                <p className="text-sm text-slate-700 mt-1">
                  Email{" "}
                  <a href="mailto:hello@cyannect.com" className="font-semibold text-burgundy-700 hover:underline">
                    hello@cyannect.com
                  </a>
                  {" "}or visit{" "}
                  <Link to="/pricing" className="font-semibold text-burgundy-700 hover:underline">
                    /pricing
                  </Link>
                  {" "}for the current plan catalogue.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
